'use client';

import CountDownEntity from '../../entities/CountDown/CountDown';
import { dpAssert } from '@/utils/helpers';
import { action, makeObservable, observable } from 'mobx';
import audioPlayerModel from '../AudioPlayer/AudioPlayerModel';
// import rendererSheetModel from "../RendererSheet/RendererSheetModel";
import samplesSelectorModel from '../Samples/SamplesSelector/SamplesSelectorModel';
import { apiSwagger } from '../../shared/api';
import { isDebugPage } from '@/sharedTypes/helpers';
import { PlayerState } from '@/sharedTypes/main';
import { ManagerEntity } from '@/shared/globalFunctions';
import { triggerEvent } from '@/sharedTypes/pureHelpers';
import toast from '../ui/custom-toast';
import { t } from '@/shared/i18n';

class PlaybackControlModel {
  blindMode = false;
  setBlindMode(value: boolean) {
    this.blindMode = value;
    dpAssert(window.rendererSheetModel);
    // if we are in the practice mode or play mode
    if (ManagerEntity.player?.state != PlayerState.STOPPED) {
      window.rendererSheetModel.hidden = this.blindMode;
      return;
    }
  }
  constructor() {
    makeObservable(this, {
      blindMode: observable,
      triggerBlind: action,
    });

    // Initialize global instance only in browser environment
    if (typeof window !== 'undefined') {
      (window as any).playbackControlModel = this;
    }
  }
  playStartBase() {
    const manager = ManagerEntity;
    const sampleIdentifier = samplesSelectorModel.selectedSample?.identifier;
    if (manager.player && sampleIdentifier) {
      manager.player.eventsRecorder.startRecording(sampleIdentifier);
    }
    if (!ManagerEntity.midiManager.input) {
      toast({
        type: 'error',
        title: t('toast.attention_title'),
        description: t('toast.missing_input_desc'),
        button: {
          label: t('toast.open_midi_helper'),
          onClick: () => {
            triggerEvent('openMidiHelper');
          }
        },
        duration: 10000
      });
    }
  }
  async startPractice() {
    console.error('[PlaybackControlModel] startPractice is deprecated.');
  }

  startPlay(instantAndWithoutPlay = false) {
    const manager = ManagerEntity;
    const player = ManagerEntity.player;

    if (!manager) {
      console.error('ManagerEntity is not initialized');
      throw new Error('ManagerEntity is not initialized');
    }

    if (!player) {
      console.error('Player is not initialized');
      throw new Error('Player is not initialized');
    }

    console.log('ManagerEntity and Player are initialized', {
      manager,
      player,
    });

    this.playStartBase();

    const startPlayCallback = () => {
      const player = ManagerEntity.player;
      dpAssert(player);

      // player.state = PlayerState.PLAYING;
      console.log('*Player state before startSelectedMode():', player.state);
      player.startSelectedMode();
      player.state = PlayerState.PLAYING;
      console.log('*Player state after startSelectedMode():', player.state);

      if (this.blindMode) {
        window.rendererSheetModel.hidden = true;
      }

      console.log('*Triggering playStarted event');
      triggerEvent('playStarted');
      console.log('*playStarted event triggered');
    };

    if (isDebugPage()) {
      instantAndWithoutPlay = true;
    }

    if (instantAndWithoutPlay) {
      console.log('*Starting playback instantly without countdown');
      startPlayCallback();
    } else {
      console.log('*Starting countdown before playback');
      CountDownEntity.startCountDown(() => {
        console.log('*Countdown completed, starting playback');
        startPlayCallback();
      });
    }
  }

  triggerBlind() {
    this.setBlindMode(!this.blindMode);
    window.rendererSheetModel.hidden = this.blindMode;
  }
  pause() {
    this.setBlindMode(false);
    window.rendererSheetModel.hidden = false;

    const player = ManagerEntity.player;
    const metronome = ManagerEntity.metronome;
    audioPlayerModel.pause();
    if (player) {
      player.state = PlayerState.PAUSE;
      player.pause();
      metronome.playOff();
    }

    CountDownEntity.resetCountDown();
  }
  rewind() {
    const player = ManagerEntity.player;
    const metronome = ManagerEntity.metronome;
    audioPlayerModel.rewind();
    if (player) {
      player.rewind(true);
      metronome.playOff();
    }
    if (player) {
      if (ManagerEntity.selectedPracticeMode?.id === 1) {
        player.state = PlayerState.REWIND;
      }
    }
  }

  async stop() {
    const player = ManagerEntity.player;
    const metronome = ManagerEntity.metronome;
    audioPlayerModel.stop();
    dpAssert(player);
    if (this.blindMode) {
      // stop without reset
      await player.stop(false);
    } else {
      await player.stop();
    }
    metronome.playOff();
    player.state = PlayerState.STOPPED;

    this.setBlindMode(false);
    window.rendererSheetModel.hidden = false;

    await (apiSwagger.debugEvents.autosaveCreate as any)(
      player.eventsRecorder.debugData
    );
    player?.eventsRecorder.reset();

    player?.eventsRecorder.stopRecording();

    CountDownEntity.resetCountDown();

    return Promise.resolve();
  }
  async playEnded() {
    const player = ManagerEntity.player;
    audioPlayerModel.stop();
    if (player) {
      await player.stop(false);
    }
    this.setBlindMode(false);
    window.rendererSheetModel.hidden = false;
  }
  async triggerRedNotes() {
    const selectedPracticeMode = ManagerEntity.player?.selectedPracticeMode;
    if (selectedPracticeMode && selectedPracticeMode.id === 0) {
      const notesVisible = selectedPracticeMode.newNotesHandler.notesVisible;
      if (notesVisible) selectedPracticeMode.newNotesHandler.hideAllNotes();
      if (!notesVisible) selectedPracticeMode.newNotesHandler.showRedNotes();
    }
    return Promise.resolve();
  }
}

const playbackControlModel = new PlaybackControlModel();

export default playbackControlModel;
